/**
 * Thrown eggs (vanilla ThrownEgg / ThrowableProjectile): launched at 1.5
 * blocks/tick along the look (inaccuracy 1), gravity 0.03, drag 0.99 (0.8 in
 * water). On hitting a block or a mob it breaks: a mob takes a 0-damage hit
 * (hurt flash + knockback), and 1 in 8 eggs hatch a baby chicken — 1 in 32 of
 * those hatch four. DOM-free; render/thrown.ts draws them.
 */

import { collisionOf, IS_WATERY } from '../blocks';
import { WORLD_HEIGHT } from '../constants';
import type { World } from '../world/chunk';
import type { Mob } from './mob';

export interface Thrown {
  readonly id: number;
  x: number; y: number; z: number;
  px: number; py: number; pz: number;
  vx: number; vy: number; vz: number;
  age: number;
  dead: boolean;
}

export interface ThrownCtx {
  world: World;
  /** Nearest living mob along the segment (direction unnormalised, length maxDist). */
  raycastMob(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxDist: number): { mob: Mob; dist: number } | null;
  /** 0-damage hit with knockback away from (fromX, fromZ). */
  hitMob(mob: Mob, fromX: number, fromZ: number): void;
  spawnChick(x: number, y: number, z: number): void;
  /** Egg-shell burst at the impact. */
  shatter(x: number, y: number, z: number): void;
  /** False while the chunk at (x, z) is not loaded: the egg freezes. */
  loaded?(x: number, z: number): boolean;
}

export const EGG_SPEED = 1.5;
export const EGG_GRAVITY = 0.03;
const MAX_AGE = 1200;

export class ThrownSim {
  readonly eggs: Thrown[] = [];
  private nextId = 1;

  constructor(private readonly rnd: () => number = Math.random) {}

  /** Throw from the eye along (yaw, pitch) (player convention: forward = (-sin yaw, sin pitch, -cos yaw)). */
  throwEgg(x: number, y: number, z: number, yaw: number, pitch: number, inheritVx = 0, inheritVy = 0, inheritVz = 0): Thrown {
    const cp = Math.cos(pitch);
    let dx = -Math.sin(yaw) * cp, dy = Math.sin(pitch), dz = -Math.cos(yaw) * cp;
    const g = (): number => (this.rnd() + this.rnd() + this.rnd() - 1.5) * 0.0075 * 1.15;
    dx += g(); dy += g(); dz += g();
    const l = Math.hypot(dx, dy, dz) || 1;
    const e: Thrown = {
      id: this.nextId++, x, y, z, px: x, py: y, pz: z,
      vx: (dx / l) * EGG_SPEED + inheritVx, vy: (dy / l) * EGG_SPEED + inheritVy, vz: (dz / l) * EGG_SPEED + inheritVz,
      age: 0, dead: false,
    };
    this.eggs.push(e);
    return e;
  }

  tick(ctx: ThrownCtx): void {
    for (let i = this.eggs.length - 1; i >= 0; i--) {
      const e = this.eggs[i];
      e.px = e.x; e.py = e.y; e.pz = e.z;
      if (ctx.loaded && !ctx.loaded(e.x, e.z)) continue;
      this.step(e, ctx);
      if (e.dead || ++e.age > MAX_AGE || e.y < -64) this.eggs.splice(i, 1);
    }
  }

  private step(e: Thrown, ctx: ThrownCtx): void {
    const len = Math.hypot(e.vx, e.vy, e.vz);
    // first block along the motion
    let tBlock = Infinity;
    const steps = Math.max(1, Math.ceil(len / 0.1));
    for (let s = 1; s <= steps; s++) {
      const t = s / steps;
      const x = e.x + e.vx * t, y = e.y + e.vy * t, z = e.z + e.vz * t;
      if (y < 0 || y >= WORLD_HEIGHT) continue;
      if (solidAt(ctx.world, x, y, z)) { tBlock = t; break; }
    }
    const mob = len > 1e-6 ? ctx.raycastMob(e.x, e.y, e.z, e.vx, e.vy, e.vz, len) : null;
    const tMob = mob ? mob.dist / len : Infinity;
    if (mob && tMob <= tBlock) {
      e.x += e.vx * tMob; e.y += e.vy * tMob; e.z += e.vz * tMob;
      ctx.hitMob(mob.mob, e.x - e.vx, e.z - e.vz);
      this.breakEgg(e, ctx);
      return;
    }
    if (tBlock <= 1) {
      // back off to just before the block
      const t = Math.max(0, tBlock - 0.1 / Math.max(len, 0.1));
      e.x += e.vx * t; e.y += e.vy * t; e.z += e.vz * t;
      this.breakEgg(e, ctx);
      return;
    }
    e.x += e.vx; e.y += e.vy; e.z += e.vz;
    const inWater = IS_WATERY[ctx.world.getBlock(Math.floor(e.x), Math.floor(e.y), Math.floor(e.z))] === 1;
    const drag = inWater ? 0.8 : 0.99;
    e.vx *= drag; e.vy *= drag; e.vz *= drag;
    e.vy -= EGG_GRAVITY;
  }

  private breakEgg(e: Thrown, ctx: ThrownCtx): void {
    e.dead = true;
    ctx.shatter(e.x, e.y, e.z);
    if (this.rnd() < 1 / 8) {
      const n = this.rnd() < 1 / 32 ? 4 : 1;
      for (let k = 0; k < n; k++) ctx.spawnChick(e.x, e.y, e.z);
    }
  }

  clear(): void { this.eggs.length = 0; }
}

function solidAt(w: World, x: number, y: number, z: number): boolean {
  const bx = Math.floor(x), by = Math.floor(y), bz = Math.floor(z);
  const sh = collisionOf(w.getBlock(bx, by, bz));
  const lx = (x - bx) * 16, ly = (y - by) * 16, lz = (z - bz) * 16;
  for (let s = 0; s < sh.length; s += 6) {
    if (lx >= sh[s] && lx <= sh[s + 3] && ly >= sh[s + 1] && ly <= sh[s + 4] && lz >= sh[s + 2] && lz <= sh[s + 5]) return true;
  }
  return false;
}
